import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useObserver, useSubtree } from "subtree.js/react";
import { useSaidInView } from "../../components/BottomBar";
import { CardRow } from "../../components/CardRow";
import { InventoryTabs } from "../../components/InventoryTabs";
import { LoadMoreSentinel } from "../../components/LoadMoreSentinel";
import { LoadFailed, Loading } from "../../components/LoadState";
import { PanelHead } from "../../components/PanelHead";
import { ArchiveIcon, ClearIcon, SearchIcon } from "../../components/review-icons";
import { ScanCardsLink } from "../../components/ScanCardsLink";
import { SwipeAction } from "../../components/SwipeAction";
import { UndoToast } from "../../components/UndoToast";
import { useKeepRowsInPlace } from "../../components/use-keep-rows-in-place";
import { formatCents } from "../../core/primitives/card-display";
import { displayedAvailable } from "../../core/primitives/stock-list";
import {
  CARD_FILTERS,
  CATALOGUE_KINDS,
  CardsActions,
  CardsState,
  isUndoKey,
  noMatchText,
  stockListView,
  stockRowHeld,
} from "./cards-model";

/** Inventory's Stock pane, as iOS's: search, the filter chips, what the list is worth, and the cards. */
export function CardsPage() {
  const state = useSubtree(CardsState);
  const actions = useSubtree(CardsActions);
  const observer = useObserver();
  const searchRef = useRef<HTMLInputElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const failedRef = useRef<HTMLDivElement>(null);
  const retryRef = useRef<HTMLButtonElement>(null);
  const rowsRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  // A tap on a row that takes the sentence above away: the rows stay under the finger.
  useKeepRowsInPlace(rowsRef);

  const filter = observer.watch(state.filter);
  const kind = observer.watch(state.kind);
  const search = observer.watch(state.search);
  const status = observer.watch(state.status);
  const items = observer.watch(state.items);
  const nextCursor = observer.watch(state.nextCursor);
  const canEdit = observer.watch(state.canEditInventory);
  const stockEdits = observer.watch(state.stockEdits);
  const stockValue = observer.watch(state.stockValueCents);
  const reviewCount = observer.watch(state.reviewCount);
  const repricing = observer.watch(state.repricing);
  const outcomes = observer.watch(state.outcomes);
  const busy = observer.watch(state.busy);
  const undo = observer.watch(state.undo);
  const error = observer.watch(state.error);
  const opening = observer.watch(state.opening);
  const refreshing = observer.watch(state.refreshing);
  const scrollTo = observer.watch(state.scrollTo);
  // An archive or a count refused from a row far down the list is said at the top, and brought into view.
  useSaidInView(errorRef, error, true, false);
  // So is a list read that failed, Back's among them: it holds rows far down the list until Retry reads it.
  const failed = status.kind === "error" ? status.message : null;
  useSaidInView(failedRef, failed, true, false);
  // A row the focus was left on waits for the list too: the focus goes to Retry, the one thing that acts.
  useEffect(() => {
    if (failed === null) return;
    const active = document.activeElement;
    if (active === null || active === document.body || active === titleRef.current || rowsRef.current?.contains(active))
      retryRef.current?.focus({ preventScroll: true });
  }, [failed]);
  // Rows held while Back's list is read again say so, and say when they are free.
  const wasRefreshing = useRef(false);
  const [heldSaid, setHeldSaid] = useState("");
  useEffect(() => {
    if (refreshing) setHeldSaid("Reading your cards again. The rows wait for it.");
    else if (wasRefreshing.current) setHeldSaid("Your cards are up to date.");
    wasRefreshing.current = refreshing;
  }, [refreshing]);
  // Back from a card: the page goes where it was left, once its cards are on screen.
  useLayoutEffect(() => {
    if (scrollTo === null || items.length === 0) return;
    window.scrollTo(0, scrollTo.y);
    document.querySelector<HTMLElement>(`[data-open-card="${scrollTo.cardId}"]`)?.focus({ preventScroll: true });
    actions.placeRestored();
  }, [scrollTo, items.length, actions]);
  useEffect(() => {
    if (!undo) return;
    const onKey = (e: KeyboardEvent) => {
      const inField = e.target instanceof Element && e.target.closest("input, textarea, [contenteditable]") !== null;
      if (!isUndoKey(e, inField)) return;
      e.preventDefault();
      void actions.undo();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, actions]);

  const loading = status.kind === "idle" || status.kind === "loading";
  const view = stockListView({ count: items.length, more: Boolean(nextCursor), status, search, filter, kind });
  const filterLabel = [
    kind !== "all" ? CATALOGUE_KINDS.find((k) => k.key === kind)?.label : null,
    filter !== "all" || kind === "all" ? CARD_FILTERS.find((f) => f.key === filter)?.label : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    // The room under the list stays whether or not the Undo bar shows, so the page does not move when it goes.
    <div className="mx-auto max-w-4xl px-2 pt-4 pb-40 sm:px-6 sm:pt-8 sm:pb-24 lg:px-8">
      <div className="panel p-4 sm:p-6">
        <PanelHead className="mb-5" action={<ScanCardsLink place="head" />}>
          <h1 ref={titleRef} tabIndex={-1} className="text-lg font-bold text-white">
            Inventory
          </h1>
          <InventoryTabs active="stock" reviewCount={reviewCount} />
        </PanelHead>

        <div className="space-y-3">
          {error && (
            <div
              ref={errorRef}
              role="alert"
              className="note note-danger flex scroll-mt-12 items-start justify-between gap-3 text-danger"
            >
              <span className="min-w-0">{error}</span>
              <button type="button" onClick={actions.dismissError} className="btn-ghost shrink-0">
                Dismiss
              </button>
            </div>
          )}

          <div className="relative">
            <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-faint">
              <SearchIcon />
            </span>
            <input
              ref={searchRef}
              type="search"
              aria-label="Search your cards"
              placeholder="Search your cards"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              className="field w-full pr-10 pl-9 [&::-webkit-search-cancel-button]:appearance-none"
              value={search}
              onChange={(e) => actions.setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => {
                  actions.setSearch("");
                  searchRef.current?.focus();
                }}
                className="absolute top-1/2 right-1 flex h-9 w-9 -translate-y-1/2 items-center justify-center text-faint hover:text-muted"
              >
                <ClearIcon />
              </button>
            )}
          </div>

          {view.chips && (
            <>
              <fieldset
                aria-label="Kind"
                className="-mx-4 m-0 flex min-w-0 gap-2 overflow-x-auto border-0 px-4 py-0 [scrollbar-width:none] sm:-mx-6 sm:px-6 [&::-webkit-scrollbar]:hidden"
              >
                {CATALOGUE_KINDS.map((tab) => (
                  <button
                    type="button"
                    key={tab.key}
                    aria-pressed={kind === tab.key}
                    onClick={() => actions.setKind(tab.key)}
                    className={`shrink-0 rounded-full px-3 py-[7px] text-[13px] whitespace-nowrap transition-colors ${
                      kind === tab.key
                        ? "bg-accent font-semibold text-ink"
                        : "bg-surface font-medium text-muted hover:bg-surface-strong hover:text-white"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </fieldset>

              <fieldset
                aria-label="Show"
                className="-mx-4 m-0 flex min-w-0 gap-2 overflow-x-auto border-0 px-4 py-0 [scrollbar-width:none] sm:-mx-6 sm:px-6 [&::-webkit-scrollbar]:hidden"
              >
                {CARD_FILTERS.map((tab) => (
                  <button
                    type="button"
                    key={tab.key}
                    aria-pressed={filter === tab.key}
                    onClick={() => actions.setFilter(tab.key)}
                    className={`shrink-0 rounded-full px-3 py-[7px] text-[13px] whitespace-nowrap transition-colors ${
                      filter === tab.key
                        ? "bg-accent font-semibold text-ink"
                        : "bg-surface font-medium text-muted hover:bg-surface-strong hover:text-white"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </fieldset>

              <div className="flex items-baseline justify-between gap-3 pt-1">
                <p className="text-[11px] font-semibold tracking-[0.04em] text-faint uppercase">
                  {items.length}
                  {nextCursor ? "+" : ""} {items.length === 1 ? "card" : "cards"}
                </p>
                {stockValue !== null && (
                  <p className="text-xs tabular-nums">
                    <span className="text-muted">Value </span>
                    <span className="font-semibold text-accent">{formatCents(stockValue)}</span>
                  </p>
                )}
              </div>
            </>
          )}

          {status.kind === "error" && (
            <div ref={failedRef} className="scroll-mt-12">
              <LoadFailed
                title="Couldn't load your cards."
                message={status.message}
                onRetry={() => {
                  // Retry goes with the failure as the list is read: the focus waits on the title, or on Back's row.
                  titleRef.current?.focus({ preventScroll: true });
                  actions.retry();
                }}
                retryRef={retryRef}
              />
            </div>
          )}

          {view.body === "loading" ? (
            <Loading label="Loading your cards…" />
          ) : view.body === "no-match" ? (
            <div className="py-5 text-center">
              <p className="text-sm font-semibold text-muted">No cards match</p>
              <p className="mt-1 text-[13px] text-faint">{noMatchText(search, filterLabel)}</p>
            </div>
          ) : view.body === "nothing-yet" ? (
            <div className="px-6 pt-14 pb-6 text-center">
              <p className="text-[17px] font-semibold text-muted">Nothing scanned yet</p>
              <p className="mx-auto mt-2 max-w-sm text-sm text-faint">
                Scan a card off the shelf. You price it, and it goes on sale to anyone standing in the shop.
              </p>
            </div>
          ) : (
            <div ref={rowsRef} className="space-y-2">
              <p role="status" className="sr-only">
                {heldSaid}
              </p>
              {items.map((item) => {
                const held = stockRowHeld(item.card.id, { busy, opening, refreshing });
                return (
                  <SwipeAction
                    key={item.card.id}
                    enabled={canEdit && !held}
                    label="Archive"
                    icon={<ArchiveIcon size={18} />}
                    onAction={() => void actions.archive(item.card)}
                  >
                    <CardRow
                      item={item}
                      available={displayedAvailable(item.card, stockEdits)}
                      canEdit={canEdit}
                      busy={held}
                      repricing={repricing.has(item.card.id)}
                      opening={opening === item.card.id}
                      openHeld={opening !== null}
                      outcome={outcomes[item.card.id] ?? null}
                      onOpen={(section) =>
                        actions.openCard(item.card, section, typeof window === "undefined" ? 0 : window.scrollY)
                      }
                      onStep={(step) => actions.stepStock(item.card, step)}
                      onReprice={() => void actions.reprice(item.card)}
                      onArchive={() => void actions.archive(item.card)}
                    />
                  </SwipeAction>
                );
              })}
              {/* Also under no rows: a filtered page can come back empty with more pages after it. */}
              {nextCursor && status.kind !== "error" && (
                <LoadMoreSentinel onVisible={() => void actions.loadMore()} loading={loading} />
              )}
            </div>
          )}
        </div>
      </div>

      {undo && (
        <UndoToast
          title={undo.title}
          change={undo.change}
          onUndo={() => void actions.undo()}
          onHold={actions.holdUndo}
          onRelease={actions.releaseUndo}
        />
      )}
      <ScanCardsLink />
    </div>
  );
}
